import React, { useEffect } from "react";
import { SearchModal } from "~/widgets/SearchModal";
import { useFocusContext } from "~/contexts/FocusContext";
import { useCharacterStore } from "~/stores/characterStore";
import styles from "~/widgets/TableSearch/TableSearch.module.scss";
import type { Table } from "~/types/storage";

export interface TableSearchProps {
  isOpen: boolean;
  onClose: () => void;
  tables: Table[];
  activeTableId: string;
  onSelect: (tableId: string) => void;
  onCreateNew?: (name: string) => void;
}

/**
 * TableSearch Component
 *
 * A floating search modal that allows users to quickly find and switch between tables.
 *
 * Features:
 * - Keyboard navigation (up/down arrows, enter, escape)
 * - Fuzzy search by table name
 * - Shows character count per table
 * - "Create new table" via empty submit (if onCreateNew provided)
 */
const TableSearch: React.FC<TableSearchProps> = ({
  isOpen,
  onClose,
  tables,
  activeTableId,
  onSelect,
  onCreateNew,
}) => {
  const { setIsOverlayOpen } = useFocusContext();
  const characters = useCharacterStore((state) => state.characters);

  // Notify FocusContext when overlay opens/closes
  useEffect(() => {
    setIsOverlayOpen(isOpen);
  }, [isOpen, setIsOverlayOpen]);

  const filterFn = (table: Table, query: string): boolean => {
    return table.name.toLowerCase().includes(query);
  };

  const handleSelect = (table: Table) => {
    onSelect(table.id);
  };

  const renderItem = (table: Table, isSelected: boolean) => {
    const isActive = table.id === activeTableId;
    const charCount = characters.filter(
      (c) => c.belongsToTable === table.id
    ).length;

    return (
      <div className={styles.tableInfo}>
        <div className={styles.nameRow}>
          <span
            className={`${styles.tableName} ${isSelected ? styles.tableNameSelected : ""} ${isActive ? styles.tableNameActive : ""}`}
          >
            {table.name}
          </span>
          {isActive && <span className={styles.activeBadge}>Active</span>}
        </div>
        <div className={styles.statsRow}>
          <span className={styles.stat}>
            {charCount} {charCount === 1 ? "character" : "characters"}
          </span>
        </div>
      </div>
    );
  };

  const handleEmptySubmit = onCreateNew
    ? (query: string) => {
      if (query.trim()) {
        onCreateNew(query.trim());
        onClose();
      }
    }
    : undefined;

  const emptyHintText = onCreateNew
    ? 'Press ↵ to create new table'
    : 'Try a different search term';

  return (
    <SearchModal<Table>
      isOpen={isOpen}
      onClose={onClose}
      placeholder="Search tables..."
      items={tables}
      getItemKey={(table) => table.id}
      filterFn={filterFn}
      renderItem={renderItem}
      onSelect={handleSelect}
      onEmptySubmit={handleEmptySubmit}
      emptyMessage="No tables found"
      emptyHint={emptyHintText}
      footerHints={[
        { label: "navigate", hint: "↑↓" },
        { label: "select", hint: "↵" },
        { label: "to open", hint: "T" },
      ]}
    />
  );
};

export default TableSearch;
